/**
 * Plans and checkout.
 *
 * **Everything on this page is derived from the API, nothing is hard-coded.**
 * The plan catalogue, the prices, the feature flags and the device counts all
 * come from `/v1/billing/plans`, which reads the same `PLANS` table every gate
 * in the product reads. A pricing page that lists features from a designer's
 * copy deck eventually advertises something the backend will not honour, and
 * the user finds out after paying.
 *
 * That is not hypothetical here: the plan document advertises 6 devices on Pro,
 * the enforced number is 3 pending a licensing answer, and the API ships
 * `deviceCapNote` explaining why. It is rendered, not hidden.
 */
import { useEffect, useState } from 'react';
import type { JSX } from 'react';
import { api, ApiError, type PlanRow, type PlansResponse } from '../lib/api.js';
import { Link, navigate } from '../lib/router.js';
import { hasIdentity, useSession } from '../lib/session.js';

type Interval = 'monthly' | 'annual';

export function PricingScreen(): JSX.Element {
  const { status } = useSession();
  const [data, setData] = useState<PlansResponse | null>(null);
  const [interval, setInterval] = useState<Interval>('monthly');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void api.plans()
      .then((d) => { if (!cancelled) setData(d); })
      .catch(() => { if (!cancelled) setError('Could not load plans.'); });
    return () => { cancelled = true; };
  }, []);

  async function subscribe(plan: 'trader' | 'pro'): Promise<void> {
    setError(null);

    // Checkout needs a real session — the demo identity is not a customer, and
    // sending it to Stripe would create a subscription nobody can sign in to.
    if (status !== 'authenticated') {
      navigate('/signup');
      return;
    }

    setBusy(plan);
    try {
      const { url } = await api.checkout(plan, interval);
      // A full navigation, not a router push: Stripe Checkout is a different
      // origin and comes back through BILLING_SUCCESS_URL.
      window.location.href = url;
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not start checkout.');
      setBusy(null);
    }
  }

  if (error && !data) {
    return <div className="pricing-page"><p className="form-error" role="alert">{error}</p></div>;
  }

  if (!data) {
    return <div className="pricing-page"><p className="dim">Loading plans…</p></div>;
  }

  return (
    <div className="pricing-page">
      <header className="pricing-head">
        <Link to="/" className="auth-brand">AEGIS</Link>
        <h1 className="pricing-title">Plans</h1>
        <p className="pricing-sub">
          Deterministic chart analysis is free on every plan, forever. It is computed
          once per symbol and broadcast to everyone, so serving it costs the same
          whether you pay or not. Paid plans add the model's written read and your
          own journal statistics on the chart.
        </p>

        <div className="interval-toggle" role="group" aria-label="Billing interval">
          {(['monthly', 'annual'] as const).map((i) => (
            <button
              key={i}
              className={`tf compact ${interval === i ? 'on' : ''}`}
              onClick={() => setInterval(i)}
              aria-pressed={interval === i}
            >
              {i === 'monthly' ? 'Monthly' : 'Annual'}
            </button>
          ))}
        </div>
      </header>

      {!data.billingEnabled && (
        <div className="notice-bar" role="status">
          Billing is not configured on this deployment, so every account is on the free
          plan and checkout is unavailable. Nothing here will charge you.
        </div>
      )}

      {error && <div className="form-error" role="alert">{error}</div>}

      <div className="plan-grid">
        {data.plans.map((plan) => (
          <PlanCard
            key={plan.id}
            plan={plan}
            interval={interval}
            billingEnabled={data.billingEnabled}
            busy={busy === plan.id}
            signedIn={hasIdentity(status)}
            onSubscribe={() => void subscribe(plan.id as 'trader' | 'pro')}
          />
        ))}
      </div>

      <p className="pricing-note">{data.deviceCapNote}</p>

      {/* The data tier, said plainly, because it is the question every new
          user asks and the answer does not split the way people expect.
          Exchange-traded data is licensed per subscriber; OTC data is not. */}
      <p className="pricing-note">
        <strong>Crypto and forex charts are real-time on every plan, including
        free</strong> — nobody licenses those prices, so they cost nothing to
        show. Stocks and futures are delayed until you connect a broker,
        because exchanges license that data per subscriber.
      </p>

      <p className="pricing-note">
        Connecting your futures broker gives you real-time data at no extra
        cost to you or to us: you already pay the exchange for it, and AEGIS
        simply renders what you have. Exchange market data fees are billed by
        your broker under your own subscriber agreement, not by AEGIS. A
        non-professional subscriber is typically about $9 per exchange per
        month; a professional subscriber is substantially more.
      </p>

      <p className="auth-legal">
        AEGIS is analytics and journaling. It does not place orders, it is not a
        broker, and it is not an investment adviser.
      </p>
    </div>
  );
}

function PlanCard({
  plan, interval, billingEnabled, busy, signedIn, onSubscribe,
}: {
  plan: PlanRow;
  interval: Interval;
  billingEnabled: boolean;
  busy: boolean;
  signedIn: boolean;
  onSubscribe: () => void;
}): JSX.Element {
  const free = plan.id === 'free';
  const price = interval === 'annual' ? plan.annualUsd : plan.monthlyUsd;
  const per = interval === 'annual' ? '/year' : '/month';

  return (
    <section className={`plan-card ${plan.id === 'trader' ? 'plan-featured' : ''}`}>
      {plan.id === 'trader' && <div className="plan-flag">Most traders start here</div>}

      <h2 className="plan-name">{plan.name}</h2>

      <div className="plan-price">
        <span className="plan-amount">${price}</span>
        {!free && <span className="plan-per">{per}</span>}
      </div>

      {!free && interval === 'annual' && plan.annualSavingUsd > 0 && (
        <div className="plan-saving">Saves ${plan.annualSavingUsd} a year</div>
      )}
      {free && <div className="plan-saving">No card required</div>}

      <ul className="plan-features">
        <Feature on>Deterministic chart annotations</Feature>
        <Feature on={plan.writtenAnalysis}>Written analysis and regime call</Feature>
        <Feature on={plan.personalOverlay}>Your journal statistics on the chart</Feature>
        <Feature on={plan.propFirmRules}>Prop-firm rule tracking</Feature>
        <Feature on={plan.taxExport}>Tax export</Feature>
        <Feature on={plan.replayMode}>Replay mode</Feature>
        <Feature on={plan.journalAgents}>Journal agents</Feature>
        <Feature on={plan.backtesting}>Backtesting</Feature>
        {/* True on every plan, and shown rather than assumed. The whole journal
            being free is the positioning, so a plan card that omitted it would
            undersell the free tier to the person deciding. */}
        <Feature on={plan.journalDepth}>
          Strategies, reports, the daily loop and your score
        </Feature>
        <Feature on>
          {plan.maxBrokerAccounts === null
            ? 'Unlimited broker accounts'
            : `${plan.maxBrokerAccounts} broker account${plan.maxBrokerAccounts === 1 ? '' : 's'}`}
        </Feature>
        <Feature on>
          {plan.journalHistoryDays === null
            ? 'Full journal history'
            : `${plan.journalHistoryDays} days of journal history`}
        </Feature>
        <Feature on>
          {plan.maxAlerts === null ? 'Unlimited alerts' : `${plan.maxAlerts} alerts`}
        </Feature>
        <Feature on>
          {plan.maxDevices} market-data device{plan.maxDevices === 1 ? '' : 's'}
        </Feature>
        {plan.onDemandAnalysesPerMonth > 0 && (
          <Feature on>{plan.onDemandAnalysesPerMonth} on-demand analyses a month</Feature>
        )}
        {/* Stated in ANALYSES, not credits. "1,000 credits" tells a buyer
            nothing; "20 personal analyses a month" is the same fact in the
            unit they will spend it in. */}
        {plan.monthlyCredits > 0 && plan.analysisCost > 0 && (
          <Feature on>
            {Math.floor(plan.monthlyCredits / plan.analysisCost)} personal analyses a month
          </Feature>
        )}
      </ul>

      {free ? (
        <Link to={signedIn ? '/' : '/signup'} className="btn btn-quiet btn-block">
          {signedIn ? 'Your current floor' : 'Start free'}
        </Link>
      ) : (
        <button
          className="btn btn-primary btn-block"
          onClick={onSubscribe}
          disabled={busy || !billingEnabled}
          title={billingEnabled ? undefined : 'Billing is not configured on this deployment'}
        >
          {busy ? 'Opening checkout…' : billingEnabled ? `Choose ${plan.name}` : 'Unavailable'}
        </button>
      )}
    </section>
  );
}

/**
 * A feature row.
 *
 * The included/excluded distinction is carried by the glyph and by the text
 * colour, never by colour alone — see docs/18 §5.6.
 */
function Feature({ on, children }: { on: boolean; children: React.ReactNode }): JSX.Element {
  return (
    <li className={on ? 'feat-on' : 'feat-off'}>
      <span aria-hidden="true" className="feat-mark">{on ? '✓' : '—'}</span>
      <span className="sr-only">{on ? 'Included: ' : 'Not included: '}</span>
      {children}
    </li>
  );
}
